const API_BASE = process.env.REACT_APP_API_URL || '';
const BASE_URL = `${API_BASE}/api/v1/customer`;

function getToken() {
  return localStorage.getItem('token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || data.message || `Request failed (${res.status})`);
  }

  return res.json();
}

export async function login(email, password) {
  return request('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function getMe() {
  return request('/me');
}

export async function getActiveBookings() {
  return request('/bookings?status=active');
}

export async function getPastBookings() {
  return request('/bookings?status=past');
}

export async function getAllBookings() {
  return request('/bookings?status=all');
}

export async function createBooking(data) {
  return request('/bookings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAddresses() {
  const data = await request('/addresses');
  return Array.isArray(data) ? data : data.addresses || [];
}

export async function createAddress(data) {
  return request('/addresses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAddress(id, data) {
  return request(`/addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAddress(id) {
  return request(`/addresses/${id}`, {
    method: 'DELETE',
  });
}

export async function getTemplates() {
  const data = await request('/templates');
  return Array.isArray(data) ? data : data.templates || [];
}

export async function createTemplate(data) {
  return request('/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteTemplate(id) {
  return request(`/templates/${id}`, {
    method: 'DELETE',
  });
}

export async function getUnits() {
  const data = await request('/units');
  return Array.isArray(data) ? data : data.units || [];
}

export async function getTemperatureRequirements() {
  const data = await request('/temperature-requirements');
  return Array.isArray(data) ? data : data.temperature_requirements || [];
}

export async function getAnnouncements() {
  const data = await request('/announcements');
  return data.announcements || [];
}

export async function getInvoices() {
  const data = await request('/invoices');
  return data.invoices || [];
}

export async function downloadInvoicePdf(invoiceId) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}/invoices/${invoiceId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Kunne ikke hente faktura');
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `faktura-${invoiceId}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function getContacts() {
  const data = await request('/contacts');
  return data.contacts || [];
}

export async function createContact(data) {
  return request('/contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateContact(id, data) {
  return request(`/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteContact(id) {
  return request(`/contacts/${id}`, {
    method: 'DELETE',
  });
}

export async function getSurchargeTypes() {
  const data = await request('/surcharge-types');
  return data.surcharge_types || [];
}

export async function getBooking(id) {
  return request(`/bookings/${id}`);
}

export async function updateBooking(id, data) {
  return request(`/bookings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getBookingComments(bookingId) {
  const data = await request(`/bookings/${bookingId}/comments`);
  return data.comments || [];
}

export async function addBookingComment(bookingId, message) {
  return request(`/bookings/${bookingId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

// AI endpoints
export async function getAiSuggestions(pickupAddressId, deliveryAddressId) {
  const params = new URLSearchParams();
  if (pickupAddressId) params.set('pickup_address_id', pickupAddressId);
  if (deliveryAddressId) params.set('delivery_address_id', deliveryAddressId);
  return request(`/ai/suggestions?${params}`);
}

export async function sendAiChat(message, conversation) {
  return request('/ai/chat', {
    method: 'POST',
    body: JSON.stringify({ message, conversation }),
  });
}

export async function getAiInsights() {
  return request('/ai/insights');
}

export async function getAiProactive() {
  return request('/ai/proactive');
}
